import { useState, useEffect } from "react";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";
import logo2 from "@/assets/logo2.png";

export function Nav() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useTranslation("nav");
  const currentLang = i18n.language?.startsWith("en") ? "en" : "fr";

  const toggleLang = () => {
    const next = currentLang === "fr" ? "en" : "fr";
    i18n.changeLanguage(next);
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "unset";
  }, [isMobileMenuOpen]);

  // Flat nav links
  const links = [
    { href: "/#expertise", label: t("about") },
    { href: "/forage-surface", label: t("surface_short") },
    { href: "/forage-souterrain", label: t("underground_short") },
    { href: "/forage-eau", label: t("water_short") },
    { href: "/equipements", label: t("equipment") },
    { href: "/management", label: t("management") },
    { href: "/contact", label: t("contact") },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
          ? "bg-surface/95 backdrop-blur-md border-b border-surface-foreground/10 shadow-sm"
          : "bg-transparent"
        }`}
    >
      <div
        className={`mx-auto max-w-7xl px-6 flex items-center justify-between text-surface-foreground transition-all duration-300 ${isScrolled ? "py-2" : "py-4"
          }`}
      >
        {/* Logo */}
        <a href="/" className="flex items-center -my-6 shrink-0">
          <img
            src={logo2}
            alt="Salmamy Drill"
            className="h-28 sm:h-32 w-auto object-contain drop-shadow-lg transition-all duration-300"
          />
        </a>

        {/* Desktop — flat links separated by thin dividers */}
        <nav className="hidden xl:flex items-center text-[0.7rem] font-bold tracking-[0.12em] uppercase">
          {links.map((link, i) => (
            <span key={link.href} className="flex items-center">
              <a
                href={link.href}
                className="px-3 py-2 hover:text-primary transition whitespace-nowrap"
              >
                {link.label}
              </a>
              {i < links.length - 1 && (
                <span className="w-px h-3 bg-surface-foreground/20" />
              )}
            </span>
          ))}
        </nav>

        {/* Right side actions */}
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={toggleLang}
            className="hidden xl:flex items-center border border-surface-foreground/20 text-surface-foreground/70 hover:text-primary hover:border-primary px-3 py-1.5 text-xs font-bold tracking-widest uppercase transition"
            aria-label="Switch language"
          >
            {currentLang === "fr" ? "EN" : "FR"}
          </button>
          <a
            href="/contact"
            className="hidden xl:inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 text-xs font-bold tracking-widest uppercase hover:bg-primary/90 transition"
          >
            {t("cta")} <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
          <button
            className="xl:hidden p-2 text-surface-foreground hover:text-primary transition"
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Ouvrir le menu"
          >
            <Menu className="w-8 h-8" />
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 min-h-screen bg-surface z-50 overflow-y-auto transition-transform duration-500 ease-in-out xl:hidden ${isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        <div className="px-6 py-4 flex items-center justify-between border-b border-surface-foreground/10">
          <a href="/" className="flex items-center -my-6" onClick={() => setIsMobileMenuOpen(false)}>
            <img src={logo2} alt="Salmamy Drill" className="h-20 w-auto object-contain drop-shadow-lg" />
          </a>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleLang}
              className="border border-surface-foreground/30 text-surface-foreground/70 hover:text-primary hover:border-primary px-3 py-1.5 text-xs font-bold tracking-widest uppercase transition"
            >
              {currentLang === "fr" ? "EN" : "FR"}
            </button>
            <button
              className="p-2 text-surface-foreground hover:text-primary transition bg-surface-foreground/5 rounded-full"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Fermer le menu"
            >
              <X className="w-7 h-7" />
            </button>
          </div>
        </div>

        <div className="flex flex-col px-6 py-8 font-display font-bold uppercase tracking-wide text-surface-foreground">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-xl hover:text-primary transition py-4 border-b border-surface-foreground/10 last:border-0"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="px-6 mt-4">
          <a
            href="/contact"
            onClick={() => setIsMobileMenuOpen(false)}
            className="inline-flex w-full justify-center items-center gap-2 bg-primary text-primary-foreground px-5 py-4 text-sm font-bold tracking-widest uppercase hover:bg-primary/90 transition shadow-xl"
          >
            {t("cta")} <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </header>
  );
}
